import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createGuidance,
  deleteGuidance,
  getGuidance,
  updateGuidance,
  GuidanceCreatePayload,
} from '@/api/guidanceService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

const errorMessage = (error: unknown) => {
  const res = (error as { response?: { data?: { message?: string } } })?.response;
  if (res?.data?.message) return res.data.message;
  return error instanceof Error ? error.message : 'Une erreur est survenue';
};

const PAGE_SIZE = 20;

export const useGuidance = (filters: { search?: string; sport?: string; page: number }) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'pv2', 'guidance'] });

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.pv2Guidance(filters),
    queryFn: () =>
      getGuidance({ search: filters.search, sport: filters.sport, page: filters.page, pageSize: PAGE_SIZE }),
  });

  const createMutation = useMutation({
    mutationFn: (payload: GuidanceCreatePayload) => createGuidance(payload),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Contenu enregistré', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      themeId,
      sportIdentifier,
      payload,
    }: {
      themeId: string;
      sportIdentifier: string;
      payload: {
        coachNote?: string | null;
        nutrition?: string | null;
        warmupPattern?: string | null;
        cooldownPattern?: string | null;
      };
    }) => updateGuidance(themeId, sportIdentifier, payload),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Contenu mis à jour', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ themeId, sportIdentifier }: { themeId: string; sportIdentifier: string }) =>
      deleteGuidance(themeId, sportIdentifier),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Contenu supprimé', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    pageSize: PAGE_SIZE,
    isLoading,
    createGuidance: createMutation.mutateAsync,
    updateGuidance: updateMutation.mutateAsync,
    deleteGuidance: deleteMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending,
    invalidate,
  };
};
